import React from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    FileText,
    Calendar,
    FileCheck,
    BarChart2,
    LineChart,
    MessageSquare,
    Bell,
    Clock,
    CheckCircle
} from 'lucide-react';

function HomePage() {
    const navigate = useNavigate();

    const features = [
        {
            icon: <FileText size={24} />,
            title: "Effortless Record Management",
            description: "Access and manage patient records with ease. Organize, search, and retrieve patient information in seconds."
        },
        {
            icon: <Calendar size={24} />,
            title: "Customizable Appointments",
            description: "Create flexible appointment schedules that work for your practice. Set availability, duration, and appointment types."
        },
        {
            icon: <FileCheck size={24} />,
            title: "Digital Prescriptions",
            description: "Send digital prescriptions directly to patients. Reduce errors and save time with our secure prescription system."
        },
        {
            icon: <BarChart2 size={24} />,
            title: "Clinic Performance Tracking",
            description: "Monitor your clinic's performance and growth with comprehensive analytics and reporting tools."
        },
        {
            icon: <LineChart size={24} />,
            title: "Daily Insights",
            description: "Get daily insights on what's working for your practice. Make data-driven decisions to improve patient care."
        },
        {
            icon: <MessageSquare size={24} />,
            title: "Appointment Notifications",
            description: "Automated SMS notifications for appointment confirmations and rescheduling sent directly to patients."
        },
        {
            icon: <Bell size={24} />,
            title: "Daily Appointment Reminders",
            description: "Receive daily SMS reminders about your upcoming appointments, ensuring you're always prepared."
        },
        {
            icon: <Clock size={24} />,
            title: "Patient Reminders",
            description: "Automated appointment reminders sent to patients, reducing no-shows and improving clinic efficiency."
        }
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="hero-section py-5">
                <Container>
                    <Row className="align-items-center py-5">
                        <Col lg={6} className="mb-5 mb-lg-0">
                            <h1 className="display-4 fw-bold mb-3">Transform Your Medical Practice</h1>
                            <p className="lead mb-4">
                                Streamline your clinic operations, enhance patient care, and grow your practice with our all-in-one digital solution for healthcare professionals.
                            </p>
                            <div className="d-flex gap-3">
                                <Button
                                    variant="light"
                                    className="rounded-pill px-4"
                                    onClick={() => navigate('/request-demo')}
                                >
                                    Request Demo
                                </Button>
                                <Button
                                    variant="outline-light"
                                    className="rounded-pill px-4"
                                    href="#features"
                                >
                                    Explore Features
                                </Button>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <img
                                src="https://storage.googleapis.com/vtt-production-frontend/frontend-hero.webp?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                                alt="Doctor using digital tablet with purple and blue lighting"
                                className="img-fluid rounded-4 shadow"
                            />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Features Section */}
            <section className="py-5" id="features">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3" style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Powerful Features for Modern Healthcare</h2>
                        <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
                            Our platform offers everything you need to run an efficient, patient-focused medical practice.
                        </p>
                    </div>
                    <Row className="g-4">
                        {features.map((feature, index) => (
                            <Col md={6} lg={3} key={index}>
                                <Card className="feature-card h-100 border-0 shadow-sm">
                                    <Card.Body className="p-4 text-center">
                                        <div className="feature-icon mb-3">
                                            {feature.icon}
                                        </div>
                                        <Card.Title className="fw-bold mb-3">{feature.title}</Card.Title>
                                        <Card.Text className="text-muted">{feature.description}</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Benefits Section */}
            <section className="py-5 bg-light" id="benefits">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-4 mb-lg-0">
                            <img
                                src="https://storage.googleapis.com/vtt-production-frontend/frontend-benefits.webp?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                                alt="Doctor with patient"
                                className="img-fluid rounded-4 shadow"
                            />
                        </Col>
                        <Col lg={6}>
                            <h2 className="display-5 fw-bold mb-4" style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Why Doctors Choose Us</h2>
                            <div className="d-flex mb-4">
                                <div className="me-3" style={{ color: '#6cc3f4' }}>
                                    <CheckCircle size={24} />
                                </div>
                                <div>
                                    <h4 className="fw-bold">Save Time</h4>
                                    <p className="text-muted">Reduce administrative work by up to 70% with our automated systems.</p>
                                </div>
                            </div>
                            <div className="d-flex mb-4">
                                <div className="me-3" style={{ color: '#80abe9' }}>
                                    <CheckCircle size={24} />
                                </div>
                                <div>
                                    <h4 className="fw-bold">Improve Patient Experience</h4>
                                    <p className="text-muted">Enhance patient satisfaction with convenient digital tools and timely communication.</p>
                                </div>
                            </div>
                            <div className="d-flex mb-4">
                                <div className="me-3" style={{ color: '#b26dcd' }}>
                                    <CheckCircle size={24} />
                                </div>
                                <div>
                                    <h4 className="fw-bold">Grow Your Practice</h4>
                                    <p className="text-muted">Leverage analytics and insights to make data-driven decisions for practice growth.</p>
                                </div>
                            </div>
                            <div className="d-flex">
                                <div className="me-3" style={{ color: '#fa15a6' }}>
                                    <CheckCircle size={24} />
                                </div>
                                <div>
                                    <h4 className="fw-bold">Enhance Clinical Outcomes</h4>
                                    <p className="text-muted">Focus more on patient care with streamlined administrative processes.</p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Stats Section */}
            <section className="py-5" style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)' }}>
                <Container>
                    <Row className="text-center g-4 text-white">
                        <Col md={4}>
                            <h2 className="display-4 fw-bold">95%</h2>
                            <p className="lead">Reduction in No-Shows</p>
                        </Col>
                        <Col md={4}>
                            <h2 className="display-4 fw-bold">3.5x</h2>
                            <p className="lead">ROI for Practices</p>
                        </Col>
                        <Col md={4}>
                            <h2 className="display-4 fw-bold">30%</h2>
                            <p className="lead">Time Saved Daily</p>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="py-5" style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)' }}>
                <Container className="py-4">
                    <Row className="align-items-center">
                        <Col lg={8} className="mx-auto text-center">
                            <h2 className="display-5 fw-bold mb-3 text-white">Ready to Transform Your Practice?</h2>
                            <p className="lead mb-4 text-white">
                                Join thousands of healthcare professionals who are streamlining their practice and improving patient care.
                            </p>
                            <Button
                                variant="light"
                                size="lg"
                                className="rounded-pill px-5 py-3 fw-bold"
                                onClick={() => navigate('/request-demo')}
                            >
                                Request Your Free Demo Today
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
}

export default HomePage;